import React, { useState, useEffect } from 'react';
import './App.css';
import GraphVisualization from './GraphVisualization';

interface WordData {
  word: string;
  origin: string;
  graph: any;
}

const mockWordData: WordData = {
  word: "chocolate",
  origin: "Nahuatl",
  graph: [
    { index: 0, next: 1, lang: "English", word: "chocolate" },
    { index: 1, next: 2, lang: "Spanish", word: "chocolate" },
    { index: 2, next: null, lang: "Nahuatl", word: "xocolatl" }
  ]
};

const mockOrigins: string[] = [
  "Latin", "Greek", "French", "German", "Spanish", "Italian", 
  "Old English", "Middle English", "Arabic", "Persian", "Sanskrit", 
  "Hebrew", "Celtic", "Norse", "Dutch", "Portuguese", "Nahuatl",
  "Chinese", "Japanese", "Russian", "Turkish"
];

const isDevelopment = process.env.NODE_ENV === 'development';

function App() {
  const [currentWord, setCurrentWord] = useState<string>('');
  const [wordOrigin, setWordOrigin] = useState<string>('');
  const [wordGraph, setWordGraph] = useState<any>(null);
  const [availableOrigins, setAvailableOrigins] = useState<string[]>([]);
  const [selectedOrigin, setSelectedOrigin] = useState<string>('');
  const [guessedOrigins, setGuessedOrigins] = useState<string[]>([]);
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [attemptsLeft, setAttemptsLeft] = useState<number>(6);

  // Fetch word and origins on component mount
  useEffect(() => {
    fetchWord();
    fetchOrigins();
  }, []);

  const fetchWord = async () => {
    if (isDevelopment) {
      setCurrentWord(mockWordData.word);
      setWordOrigin(mockWordData.origin);
      setWordGraph(mockWordData.graph);
      return;
    }

    try {
      const response = await fetch('https://api.guidance-rubrics.com/training/getWord');
      const data: WordData = await response.json();
      setCurrentWord(data.word);
      setWordOrigin(data.origin);
      setWordGraph(data.graph);
    } catch (error) {
      console.error('Error fetching word:', error);
    }
  };

  const fetchOrigins = async () => {
    if (isDevelopment) {
      setAvailableOrigins(mockOrigins);
      return;
    }

    try {
      const response = await fetch('https://api.guidance-rubrics.com/training/getAllOrigins');
      const data: string[] = await response.json();
      setAvailableOrigins(data);
    } catch (error) {
      console.error('Error fetching origins:', error);
    }
  };

  const handleSelect = () => {
    if (!selectedOrigin) return;

    if (selectedOrigin === wordOrigin) {
      setGameStatus('won');
    } else {
      setGuessedOrigins([...guessedOrigins, selectedOrigin]);
      setAvailableOrigins(availableOrigins.filter(origin => origin !== selectedOrigin));
      setSelectedOrigin('');
      setAttemptsLeft(attemptsLeft - 1);

      if (attemptsLeft === 1) {
        setGameStatus('lost');
      }
    }
  };

  const resetGame = () => {
    setCurrentWord('');
    setWordOrigin('');
    setWordGraph(null);
    setGuessedOrigins([]);
    setSelectedOrigin('');
    setGameStatus('playing');
    setAttemptsLeft(6);
    fetchWord();
    fetchOrigins();
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>Guess the Origin</h1>
        {(gameStatus === 'won' || gameStatus === 'lost') && wordGraph && <GraphVisualization graph={wordGraph} currentWord={currentWord} />}
        {gameStatus === 'playing' && guessedOrigins.length > 0 && wordGraph && 
          guessedOrigins.some(origin => wordGraph.some((node: any) => node.lang.toLowerCase() === origin.toLowerCase())) && 
          <GraphVisualization 
            graph={wordGraph} 
            currentWord={currentWord} 
            revealedOrigins={guessedOrigins.filter(origin => wordGraph.some((node: any) => node.lang.toLowerCase() === origin.toLowerCase()))} 
          />
        }
        {gameStatus !== 'won' && gameStatus !== 'lost' && currentWord && 
          !(gameStatus === 'playing' && guessedOrigins.length > 0 && wordGraph && 
            guessedOrigins.some(origin => wordGraph.some((node: any) => node.lang.toLowerCase() === origin.toLowerCase()))) && 
          <h2>{currentWord}</h2>
        }
        
        {gameStatus === 'playing' && (
          <div className="game-container">
            <select 
              value={selectedOrigin} 
              onChange={(e) => setSelectedOrigin(e.target.value)}
              className="origin-select"
            >
              <option value="">Select an origin</option>
              {availableOrigins.map((origin) => (
                <option key={origin} value={origin}>
                  {origin}
                </option>
              ))}
            </select>
            <button onClick={handleSelect} className="select-button">
              Select
            </button>
            <p>Attempts left: {attemptsLeft}</p>
            {guessedOrigins.length > 0 && (
              <div className="guessed-origins">
                <h3>Guessed Origins:</h3>
                <ul>
                  {guessedOrigins.map((origin) => (
                    <li key={origin}>{origin}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {gameStatus === 'won' && (
          <div className="game-result">
            <h2>You win!</h2>
            <button onClick={resetGame} className="reset-button">
              Play Again
            </button>
          </div>
        )}

        {gameStatus === 'lost' && (
          <div className="game-result">
            <h2>You lose!</h2>
            <p>The correct origin was: {wordOrigin}</p>
            <button onClick={resetGame} className="reset-button">
              Play Again
            </button>
          </div>
        )}
      </header>
    </div>
  );
}

export default App;
